/*
 * Copyright (c) 2022. American Express Company
 */

import React from 'react';

import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Card from 'src/components/atomic/atoms/card/Card';
import Circle from 'src/components/atomic/atoms/circle/Circle';
import colors from 'src/styles/colors.module.scss';
import layout from 'src/styles/layout.module.scss';
import vars from 'src/styles/variables.module.scss';
import { ReactComponent as ArrowSvg } from 'src/assets/svg/arrow.svg';
import { GlyphMinus } from '@americanexpress/dls-react';

import { props, sectionProps } from 'src/types/view-models/statsBox.view.model';
import bgColors from 'src/styles/bgColors.module.scss';
import styles from './insightsStatsBox.module.scss';
import LiteralsService from 'src/services/literals/LiteralsService';
import { CardVariant } from 'src/types/card-variant.enum';
import Selectors from 'src/utils/selectors';

function getAmountString(difference: number = 0): string {
    return `${difference.isPositive() ? '+' : ''}${difference.toDetailedDollarsString()}`;
}

function getAverageString(average: number = 0): string {
    return `${average.isPositive() ? '+' : ''}${average}%`;
}

const Section = ({
    title,
    difference,
    percentChange,
    globalPercentChange,
    'data-cy': dataCy
}: sectionProps): JSX.Element => {
    const percentChangeOutput = (): JSX.Element => {
        if (percentChange > 0) {
            return (
                <>
                    <Typography component="p" variant={TypographyVariants.HEADING_XL} className={colors.good}>
                        {percentChange}%
                    </Typography>
                    <Circle diameter={'32px'} className={bgColors.goodWeak}>
                        <ArrowSvg style={{ stroke: vars.good }} />
                    </Circle>
                </>
            );
        } else if (percentChange < 0) {
            return (
                <>
                    <Typography component="p" variant={TypographyVariants.HEADING_XL} className={colors.bad}>
                        {percentChange}%
                    </Typography>
                    <Circle diameter={'32px'} className={bgColors.badWeak}>
                        <ArrowSvg style={{ stroke: vars.bad, transform: 'rotate(180deg)' }} />
                    </Circle>
                </>
            );
        } else {
            return (
                <>
                    <Typography component="p" variant={TypographyVariants.HEADING_XL} className={colors.neutralStrong}>
                        0%
                    </Typography>
                    <Circle diameter={'32px'} className={bgColors.backgroundBar}>
                        <GlyphMinus className={colors.neutralStrong} />
                    </Circle>
                </>
            );
        }
    };

    return (
        <div data-cy={dataCy}>
            <Typography component="h3" variant={TypographyVariants.BODY_BOLD} className={layout.nowrap}>
                {title}
            </Typography>
            <div>{percentChangeOutput()}</div>
            <Typography variant={TypographyVariants.BODY_BOLD}>{getAmountString(difference)}</Typography>
            <Typography variant={TypographyVariants.SUBTEXT}>
                Avg change {getAverageString(globalPercentChange)}
            </Typography>
        </div>
    );
};

export default function InsightsStatsBox({
    netWorthDifference,
    netWorthPercentChange,
    netWorthGlobalPercentChange,
    savingsDifference,
    savingsPercentChange,
    savingsGlobalPercentChange,
    investmentsDifference,
    investmentsPercentChange,
    investmentsGlobalPercentChange
}: props): JSX.Element {
    return (
        <Card variant={CardVariant.SECONDARY} className={styles.statsBox} row>
            <Section
                title={LiteralsService.getLiteral('Insights.statsBox.changeInNetWorth')}
                data-cy={Selectors.PlanAhead.STATSBOX_NET_WORTH_SECTION}
                difference={netWorthDifference}
                percentChange={netWorthPercentChange}
                globalPercentChange={netWorthGlobalPercentChange}
            />
            <hr />
            <Section
                title={LiteralsService.getLiteral('Insights.statsBox.savingsChange')}
                data-cy={Selectors.PlanAhead.STATSBOX_SAVINGS_SECTION}
                difference={savingsDifference}
                percentChange={savingsPercentChange}
                globalPercentChange={savingsGlobalPercentChange}
            />
            <hr />
            <Section
                title={LiteralsService.getLiteral('Insights.statsBox.investmentsChange')}
                data-cy={Selectors.PlanAhead.STATSBOX_INVESTMENTS_SECTION}
                difference={investmentsDifference}
                percentChange={investmentsPercentChange}
                globalPercentChange={investmentsGlobalPercentChange}
            />
        </Card>
    );
}
